import { createDatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import type { Store } from '@blocksuite/store';

import type { RelationPropertyData } from './define';
import {
  getDatabaseDataSource,
  getDatabaseRowsWindow as getRowsProjectionWindow,
  type RelationRowsWindow,
  type RelationRowsWindowRequest,
  rowExistsInDataSource,
} from './row-projection';

/**
 * Info ueber eine Database im Doc
 */
export interface DatabaseInfo {
  id: string;
  title: string;
  properties: PropertyInfo[];
}

/**
 * Info ueber eine Property in einer Database
 */
export interface PropertyInfo {
  id: string;
  name: string;
  type: string;
}

/**
 * Row-Daten fuer Dropdown-Auswahl
 */
export interface RowInfo {
  id: string;
  display: string;
}

/**
 * Findet alle Databases im Doc
 */
export function findDatabasesInDoc(store: Store): DatabaseInfo[] {
  const databases = store.getBlocksByFlavour('affine:database');

  return databases.map(block => ({
    id: block.id,
    title: getDatabaseTitle(block),
    properties: getDatabaseProperties(block),
  }));
}

/**
 * Holt den Titel einer Database
 */
function getDatabaseTitle(block: any): string {
  const model = block.model ?? block;

  try {
    const dataSource = createDatabaseBlockDataSource(model);
    const title = dataSource.storageModel.props.title.toString();
    if (title && title.trim()) {
      return title;
    }
  } catch {}

  return `Database ${block.id.slice(0, 8)}`;
}

/**
 * Holt alle Properties einer Database
 */
function getDatabaseProperties(block: any): PropertyInfo[] {
  try {
    const model = block.model ?? block;
    const dataSource = createDatabaseBlockDataSource(model);
    const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;

    return propertyIds.map(id => {
      const propType = dataSource.propertyTypeGet(id);
      const propName = dataSource.propertyNameGet(id);
      return {
        id,
        name: propName ?? id,
        type: propType ?? 'unknown',
      };
    });
  } catch (e) {
    console.warn('Failed to get database properties:', e);
    return [];
  }
}

/**
 * Holt alle Rows einer Database mit Display-Werten
 */
export function getDatabaseRows(
  store: Store,
  databaseId: string,
  displayFieldId: string
): RowInfo[] {
  return getDatabaseRowsWindow(store, databaseId, displayFieldId, {
    offset: 0,
    limit: 50,
  }).rows;
}

export function getDatabaseRowsWindow(
  store: Store,
  databaseId: string,
  displayFieldId: string,
  request: RelationRowsWindowRequest
): RelationRowsWindow {
  const dataSource = getDatabaseDataSource(store, databaseId);

  if (!dataSource) {
    console.warn('Database not found:', databaseId);
    return {
      offset: request.offset ?? 0,
      limit: request.limit,
      totalCount: 0,
      hasMore: false,
      rows: [],
    };
  }

  try {
    return getRowsProjectionWindow(dataSource, displayFieldId, request);
  } catch (e) {
    console.warn('Failed to get database rows:', e);
    return {
      offset: request.offset ?? 0,
      limit: request.limit,
      totalCount: 0,
      hasMore: false,
      rows: [],
    };
  }
}

/**
 * Holt einen einzelnen Cell-Value aus einer Database
 */
export function getTargetCellValue(
  store: Store,
  targetDatabaseId: string,
  rowId: string,
  fieldId: string
): unknown {
  const dataSource = getDatabaseDataSource(store, targetDatabaseId);

  if (!dataSource) {
    return null;
  }

  try {
    return dataSource.cellValueGet(rowId, fieldId);
  } catch (e) {
    console.warn('Failed to get cell value:', e);
    return null;
  }
}

/**
 * E018-S008: Select Option Type
 */
interface SelectOption {
  id: string;
  value: string;
  color?: string;
}

/**
 * E018-S008: Format a raw value based on property type
 */
function formatRollupValue(
  rawValue: unknown,
  propertyType?: string,
  propertyData?: Record<string, unknown>
): string {
  if (rawValue === null || rawValue === undefined) return '';

  // Handle Select/Multi-Select: Convert ID to label
  if (
    (propertyType === 'select' || propertyType === 'multi-select') &&
    propertyData
  ) {
    const options = propertyData.options as SelectOption[] | undefined;
    if (options && typeof rawValue === 'string') {
      const option = options.find(o => o.id === rawValue);
      return option?.value ?? String(rawValue);
    }
    if (options && Array.isArray(rawValue)) {
      return rawValue
        .map(id => {
          const option = options.find(o => o.id === id);
          return option?.value ?? String(id);
        })
        .join(', ');
    }
  }

  // Handle Rich-Text: Extract plain text
  if (
    (propertyType === 'rich-text' || propertyType === 'text') &&
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  // Handle Checkbox
  if (propertyType === 'checkbox') {
    return rawValue ? '✓' : '✗';
  }

  // Default conversions
  if (typeof rawValue === 'string') return rawValue;
  if (typeof rawValue === 'number') return String(rawValue);
  if (typeof rawValue === 'boolean') return rawValue ? '✓' : '✗';
  if (Array.isArray(rawValue)) return rawValue.map(String).join(', ');
  if (
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  return String(rawValue);
}

/**
 * E018-S008: Holt einen formatierten Cell-Value aus einer Database
 * Handles Select/Multi-Select ID-to-label conversion, Rich-Text extraction, etc.
 */
export function getFormattedTargetCellValue(
  store: Store,
  targetDatabaseId: string,
  rowId: string,
  fieldId: string
): string {
  const dataSource = getDatabaseDataSource(store, targetDatabaseId);

  if (!dataSource) {
    return '';
  }

  try {
    const rawValue = dataSource.cellValueGet(rowId, fieldId);
    const propertyType = dataSource.propertyTypeGet(fieldId);
    const propertyData = dataSource.propertyDataGet(fieldId) as
      | Record<string, unknown>
      | undefined;

    return formatRollupValue(rawValue, propertyType, propertyData);
  } catch (e) {
    console.warn('Failed to get formatted cell value:', e);
    return '';
  }
}

/**
 * Holt Display-Werte fuer mehrere Row-IDs
 */
export function getDisplayValues(
  store: Store,
  targetDatabaseId: string,
  targetFieldId: string,
  rowIds: string[]
): Map<string, string> {
  const result = new Map<string, string>();

  if (!targetDatabaseId || !targetFieldId || rowIds.length === 0) {
    return result;
  }

  for (const rowId of rowIds) {
    const value = getTargetCellValue(
      store,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    result.set(rowId, formatDisplayValue(value));
  }

  return result;
}

/**
 * Formatiert einen Cell-Value fuer die Anzeige
 */
function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '(empty)';
  }

  if (typeof value === 'string') {
    return value || '(empty)';
  }

  if (Array.isArray(value)) {
    return value.join(', ') || '(empty)';
  }

  if (typeof value === 'object' && 'toString' in value) {
    return value.toString() || '(empty)';
  }

  return String(value);
}

/**
 * Prueft ob eine Database existiert
 */
export function databaseExists(store: Store, databaseId: string): boolean {
  const databases = store.getBlocksByFlavour('affine:database');
  return databases.some(db => db.id === databaseId);
}

/**
 * Prueft ob eine Row in einer Database existiert
 */
export function rowExists(
  store: Store,
  databaseId: string,
  rowId: string
): boolean {
  const dataSource = getDatabaseDataSource(store, databaseId);

  if (!dataSource) {
    return false;
  }

  return rowExistsInDataSource(dataSource, rowId);
}

/**
 * Erstellt eine inverse (bidirektionale) Relation in der Target-Database
 *
 * @param store - Der Store
 * @param sourceDatabaseId - ID der Quell-Database (wo die Original-Relation ist)
 * @param targetDatabaseId - ID der Ziel-Database (wo die inverse Relation erstellt wird)
 * @param backlinkName - Name der inverse Relation-Spalte
 * @param sourceRelationId - ID der Original-Relation (fuer inverseFieldId)
 * @param inverseCardinality - Cardinality der inverse Relation ('one' oder 'many')
 * @returns ID der erstellten inverse Relation-Property, oder undefined bei Fehler
 */
export function createInverseRelation(
  store: Store,
  sourceDatabaseId: string,
  targetDatabaseId: string,
  backlinkName: string,
  sourceRelationId: string,
  inverseCardinality: 'one' | 'many' = 'many',
  sourceCardinality: 'one' | 'many' = 'many',
  sourceDocId?: string // Optional: For consistency with CrossDoc
): string | undefined {
  console.log('[createInverseRelation] Called with:', {
    sourceDatabaseId,
    targetDatabaseId,
    backlinkName,
    sourceRelationId,
    inverseCardinality,
    sourceCardinality,
  });

  if (!sourceDatabaseId) {
    console.error('[createInverseRelation] ERROR: sourceDatabaseId is empty!');
  }

  const targetDataSource = getDatabaseDataSource(store, targetDatabaseId);

  if (!targetDataSource) {
    console.warn('Target database not available:', targetDatabaseId);
    return undefined;
  }

  try {
    // Finde die Title-Property der Source-Database fuer targetFieldId
    const sourceDataSource = getDatabaseDataSource(store, sourceDatabaseId);
    const sourceTitleFieldId = sourceDataSource
      ? findTitlePropertyId(sourceDataSource.storageModel)
      : 'title';

    // Erstelle die inverse Relation-Property
    // IMPORTANT: Set inverseCardinality to sourceCardinality for symmetric stealing!
    // When editing from the inverse column, we need to check if the SOURCE side
    // has cardinality 'one' to trigger the steal dialog.
    const inversePropertyData: RelationPropertyData = {
      targetDocId: sourceDocId, // Points back to source doc (for symmetric delete)
      targetDatabaseId: sourceDatabaseId, // Zeigt zurueck auf die Quell-DB
      targetFieldId: sourceTitleFieldId,
      cardinality: inverseCardinality, // Cardinality of THIS column
      inverseFieldId: sourceRelationId, // Verknuepft mit der Original-Relation
      inverseCardinality: sourceCardinality, // Cardinality of the SOURCE column (for symmetric stealing)
    };

    console.log(
      '[createInverseRelation] inversePropertyData:',
      inversePropertyData
    );

    const inversePropertyId = targetDataSource.propertyAdd('end', {
      type: 'relation',
      name: backlinkName,
    });

    if (!inversePropertyId) {
      console.error('[createInverseRelation] propertyAdd returned undefined');
      return undefined;
    }

    targetDataSource.propertyDataSet(inversePropertyId, inversePropertyData);

    console.log(
      'Created inverse relation:',
      inversePropertyId,
      'in database:',
      targetDatabaseId
    );
    return inversePropertyId;
  } catch (e) {
    console.error('Failed to create inverse relation:', e);
    return undefined;
  }
}

/**
 * Updates the inverseCardinality of the inverse relation column.
 * Called when cardinality is changed on the source column to keep them in sync.
 *
 * @param store - BlockSuite store
 * @param targetDatabaseId - Database containing the inverse column
 * @param inverseFieldId - ID of the inverse relation property
 * @param newInverseCardinality - New value for inverseCardinality
 */
export function updateInverseRelationCardinality(
  store: Store,
  targetDatabaseId: string,
  inverseFieldId: string,
  newInverseCardinality: 'one' | 'many'
): boolean {
  return updateRelationPropertyCardinalities(
    store,
    targetDatabaseId,
    inverseFieldId,
    { inverseCardinality: newInverseCardinality }
  );
}

/**
 * Updates the cardinality of the inverse relation column.
 * Called when "Backlink Type" is changed on source to sync to inverse.cardinality.
 */
export function updateInverseColumnCardinality(
  store: Store,
  targetDatabaseId: string,
  inverseFieldId: string,
  newCardinality: 'one' | 'many'
): boolean {
  return updateRelationPropertyCardinalities(
    store,
    targetDatabaseId,
    inverseFieldId,
    { cardinality: newCardinality }
  );
}

/**
 * Generic function to update cardinality fields on a relation property.
 */
function updateRelationPropertyCardinalities(
  store: Store,
  targetDatabaseId: string,
  propertyId: string,
  updates: { cardinality?: 'one' | 'many'; inverseCardinality?: 'one' | 'many' }
): boolean {
  const dataSource = getDatabaseDataSource(store, targetDatabaseId);

  if (!dataSource) {
    console.warn(
      '[updateRelationPropertyCardinalities] Target database not available:',
      targetDatabaseId
    );
    return false;
  }

  try {
    // Get current property data
    const currentData = dataSource.propertyDataGet(propertyId);
    if (!currentData) {
      console.warn(
        '[updateRelationPropertyCardinalities] Property not found:',
        propertyId
      );
      return false;
    }

    // Update with new values
    dataSource.propertyDataSet(propertyId, {
      ...currentData,
      ...updates,
    });

    console.log('[updateRelationPropertyCardinalities] Updated:', {
      targetDatabaseId,
      propertyId,
      updates,
    });

    return true;
  } catch (e) {
    console.error('[updateRelationPropertyCardinalities] Failed:', e);
    return false;
  }
}

/**
 * Findet die Title-Property ID einer Database
 */
function findTitlePropertyId(model: any): string {
  try {
    const dataSource = createDatabaseBlockDataSource(model);
    const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;

    for (const id of propertyIds) {
      const propType = dataSource.propertyTypeGet(id);
      const propName = dataSource.propertyNameGet(id);
      if (propType === 'title' || propName?.toLowerCase() === 'title') {
        return id;
      }
    }
  } catch (e) {
    // Ignore
  }
  return 'title'; // Default
}

/**
 * Zählt wie viele Cells einer Property mehr als 1 Value haben
 */
export function countMultiValueCells(
  store: Store,
  databaseId: string,
  propertyId: string
): number {
  const dataSource = getDatabaseDataSource(store, databaseId);

  if (!dataSource) {
    return 0;
  }

  try {
    const rowIds = dataSource.getLogicalRowIdsSnapshot();

    let count = 0;
    for (const rowId of rowIds) {
      const value = dataSource.cellValueGet(rowId, propertyId);
      if (Array.isArray(value) && value.length > 1) {
        count++;
      }
    }
    return count;
  } catch (e) {
    console.warn('Failed to count multi-value cells:', e);
    return 0;
  }
}

/**
 * Kürzt alle Cells einer Property auf maximal 1 Value (für many→one Änderung)
 *
 * STEALING-AWARE: Wenn inverseCardinality 'one' ist, kann jedes Target nur
 * von einer Row geclaimt werden. Die Funktion trackt bereits geclaimte Targets
 * und wählt für jede Zelle den ersten NOCH NICHT geclaimten Wert.
 *
 * @param store - BlockSuite store
 * @param databaseId - ID der Database
 * @param propertyId - ID der Relation-Property
 * @param inverseCardinality - Kardinalität der inversen Seite ('one' aktiviert stealing-awareness)
 */
export function truncateCellsToOne(
  store: Store,
  databaseId: string,
  propertyId: string,
  inverseCardinality: 'one' | 'many' = 'many'
): void {
  const dataSource = getDatabaseDataSource(store, databaseId);

  if (!dataSource) {
    return;
  }

  try {
    const rowIds = dataSource.getLogicalRowIdsSnapshot();

    // Track which targets are already claimed (for stealing-awareness)
    const claimedTargets = new Set<string>();

    for (const rowId of rowIds) {
      const value = dataSource.cellValueGet(rowId, propertyId);
      if (Array.isArray(value) && value.length > 1) {
        if (inverseCardinality === 'one') {
          // STEALING-AWARE: Find first unclaimed target
          let selectedValue: string | null = null;
          for (const targetId of value) {
            if (!claimedTargets.has(targetId)) {
              selectedValue = targetId;
              claimedTargets.add(targetId);
              break;
            }
          }
          // Set to selected value or empty if all claimed
          dataSource.cellValueChange(
            rowId,
            propertyId,
            selectedValue ? [selectedValue] : []
          );
        } else {
          // Simple truncation: just keep first value
          dataSource.cellValueChange(rowId, propertyId, [value[0]]);
        }
      } else if (
        Array.isArray(value) &&
        value.length === 1 &&
        inverseCardinality === 'one'
      ) {
        // Single value - still need to track for stealing-awareness
        claimedTargets.add(value[0]);
      }
    }
  } catch (e) {
    console.warn('Failed to truncate cells:', e);
  }
}

/**
 * E018-S008-T005: Delete a relation property from a database
 */
export function deleteRelationProperty(
  store: Store,
  databaseId: string,
  propertyId: string
): void {
  const dataSource = getDatabaseDataSource(store, databaseId);

  if (!dataSource) {
    console.warn(
      '[deleteRelationProperty] Database not available:',
      databaseId
    );
    return;
  }

  try {
    const properties = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    if (properties.includes(propertyId)) {
      dataSource.propertyDelete(propertyId);
      console.log('[deleteRelationProperty] Deleted property:', propertyId);
    }
  } catch (e) {
    console.warn('[deleteRelationProperty] Failed to delete property:', e);
  }
}
